import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import 'core-js/proposals/promise-with-resolvers';
// Provide polyfill for Promise.withResolvers for pdfjs to work in older browsers
import { Suspense } from 'react';
import { pdfjs } from 'react-pdf';
import { Provider as ReduxProvider } from 'react-redux';

import { RouteLogger } from '@/components/RouteLogger';

import {
  VITE_ENVIRONMENT,
  VITE_PROD,
  VITE_RELEASE_TIME,
} from '@/constants/configs';
import { IMAGES_TO_BE_PRELOAD } from '@/constants/ui';

import { usePreload } from '@/hooks/usePreload';

import { FullPageLoaderOverlay } from './components/Layout/FullPageLoader';
import { OverlayScroller } from './components/Layout/OverlayScroller';
import { ConfirmationProvider, DrawerTrackerProvider } from './context';
import { AppThemeProvider } from './context/AppThemeContext';
import './i18n/i18n';
import { store } from './redux';
import { ResponsiveApp } from './ResponsiveApp';
import { Environment } from './types/common';

import 'simplebar-react/dist/simplebar.min.css';

const queryClient = new QueryClient();
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export function App() {
  usePreload(IMAGES_TO_BE_PRELOAD);

  if (VITE_PROD && VITE_ENVIRONMENT === Environment.Development) {
    console.log(`[DEBUG] Deploy time: ${VITE_RELEASE_TIME}`);
  }

  return (
    <ReduxProvider store={store}>
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <AppThemeProvider>
            <DrawerTrackerProvider>
              <ConfirmationProvider>
                <OverlayScroller variant="page">
                  <Suspense fallback={<FullPageLoaderOverlay />}>
                    {import.meta.env.DEV && <RouteLogger />}
                    <ResponsiveApp />
                  </Suspense>
                </OverlayScroller>
              </ConfirmationProvider>
            </DrawerTrackerProvider>
          </AppThemeProvider>
        </LocalizationProvider>
      </QueryClientProvider>
    </ReduxProvider>
  );
}
